import React from 'react';
import { motion } from 'framer-motion';
import { useDraggable } from '@dnd-kit/core';
import type { SuspensionType, SuspensionDragData } from '@/types/suspension-connector';
import { SUSPENSION_CONFIGS, SUSPENSION_TYPES } from '@/types/suspension-connector';
import styles from './SuspensionPalette.module.css';

interface SuspensionPaletteProps {
  isVisible: boolean;
  onToggle: () => void;
}

/**
 * SuspensionPalette component - draggable suspension type buttons
 *
 * Positioned at bottom-left of canvas, shows draggable buttons for each
 * suspension type (4-3, 7-6, 9-8, 2-3). Users can drag these onto the
 * canvas to create suspensions between chords.
 *
 * Features:
 * - Slide-up animation on appearance (200ms)
 * - Pill-shaped buttons (48px × 24px, border-radius 12px)
 * - Color-coded by suspension type
 * - @dnd-kit drag integration for drag-and-drop
 * - Hover to show or toggle visibility button
 */
export const SuspensionPalette: React.FC<SuspensionPaletteProps> = ({
  isVisible,
  onToggle,
}) => {
  return (
    <motion.div
      className={styles.container}
      initial={false}
      animate={isVisible ? 'visible' : 'hidden'}
      variants={{
        visible: {
          y: 0,
          opacity: 1,
          pointerEvents: 'auto',
        },
        hidden: {
          y: 60,
          opacity: 0,
          pointerEvents: 'none',
        },
      }}
      transition={{
        duration: 0.2,
        ease: 'easeOut',
      }}
    >
      <div className={styles.paletteContent}>
        {/* Toggle button */}
        <button
          className={styles.toggleButton}
          onClick={onToggle}
          title={isVisible ? 'Hide suspensions' : 'Show suspensions'}
          aria-label="Toggle suspension palette"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {/* Suspension type buttons */}
        <div className={styles.buttonGroup}>
          {SUSPENSION_TYPES.map((suspensionType) => (
            <SuspensionButton
              key={suspensionType}
              suspensionType={suspensionType}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Individual draggable suspension button
 */
interface SuspensionButtonProps {
  suspensionType: SuspensionType;
}

const SuspensionButton: React.FC<SuspensionButtonProps> = ({ suspensionType }) => {
  const config = SUSPENSION_CONFIGS[suspensionType];

  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `suspension-palette-${suspensionType}`,
    data: {
      type: 'suspension-connector',
      suspensionType,
    } as SuspensionDragData,
    disabled: false,
  });

  return (
    <motion.button
      ref={setNodeRef}
      className={styles.button}
      style={{
        backgroundColor: config.color,
      }}
      {...listeners}
      {...attributes}
      whileHover={!isDragging ? { scale: 1.08 } : {}}
      whileTap={{ scale: 0.95 }}
      animate={isDragging ? { scale: 1.1, opacity: 0.8 } : {}}
      transition={{ duration: 0.15 }}
      title={`Drag to create a ${config.label} suspension`}
      aria-label={`${config.label} suspension`}
    >
      <span className={styles.buttonLabel}>{config.label}</span>
    </motion.button>
  );
};
